import { pantryColors } from '@pantry/ui'
import React from 'react'
import type { LoyaltyCouponLocationOption } from '../context/LoyaltyCouponsContext'
import { CouponLocationsSelect } from './CouponLocationsSelect'

const p = pantryColors

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[600],
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 6,
}

export type CouponLocationMode = 'all' | 'specific'

// Same segmented pill as `CouponCustomerEligibility` ("All customers /
// Specific customers").
const MODE_OPTIONS: ReadonlyArray<{ mode: CouponLocationMode; label: string }> =
  [
    { mode: 'all', label: 'All locations' },
    { mode: 'specific', label: 'Specific locations' },
  ]

interface Props {
  mode: CouponLocationMode
  onModeChange: (next: CouponLocationMode) => void
  options: LoyaltyCouponLocationOption[]
  selectedIds: number[]
  onChange: (next: number[]) => void
}

/**
 * "Participating Locations" section of the coupon modal (Cova / Greenline).
 * Restores the legacy Cova form's location checkboxes
 * (`pages/Coupon/CovaForm/General`) behind an "All locations / Specific
 * locations" pill. All sends every store location (mapper fallback); Specific
 * renders `CouponLocationsSelect`. The "at least one location" rule for
 * Specific is validated by the modal.
 */
export const CouponLocationsPicker: React.FC<Props> = ({
  mode,
  onModeChange,
  options,
  selectedIds,
  onChange,
}) => (
  <div>
    <div style={fieldLabelStyle}>Participating Locations</div>
    <div
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 3,
        background: p.gray[100],
        borderRadius: 8,
      }}
    >
      {MODE_OPTIONS.map(({ mode: value, label }) => {
        const active = value === mode
        return (
          <button
            key={value}
            type="button"
            onClick={() => onModeChange(value)}
            style={{
              padding: '6px 12px',
              fontSize: 12.5,
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              background: active ? p.white : 'transparent',
              color: active ? p.gray[800] : p.gray[500],
              cursor: 'pointer',
              boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
            }}
          >
            {label}
          </button>
        )
      })}
    </div>

    {mode === 'specific' ? (
      <div style={{ marginTop: 12 }}>
        <CouponLocationsSelect
          options={options}
          selectedIds={selectedIds}
          onChange={onChange}
        />
      </div>
    ) : (
      <div style={{ marginTop: 8, fontSize: 12, color: p.gray[500] }}>
        The coupon can be redeemed at every store location.
      </div>
    )}
  </div>
)
